import React, { useState } from "react";
import { api } from "../constants/api";

const ReceiptList = () => {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [data, setData] = useState<any[]>([]);
  const [noMoreData, setNoMoreData] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const buildStartDate = (d: string) => (d ? `${d}T00:00:00` : "");
  const buildEndDate = (d: string) => (d ? `${d}T23:59:59` : "");

  const fetchReceipts = async (pageNumber: number) => {
    if (!startDate || !endDate) {
      setError("Please select both Start Date and End Date");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const body = {
        PageNumber: pageNumber,
        PageSize: itemsPerPage,
        IsAdmin: true,
        StartDate: buildStartDate(startDate),
        EndDate: buildEndDate(endDate),
      };
      const response = await api(true, `/admin/receipt-list`, "post", body);
      console.log("🚀 ~ fetchReceipts ~ response:", response);

      // Upstream typically responds: { data: { isSuccess, result: [...], errorMessage } }
      const payload = response?.data?.data ?? response?.data ?? {};
      const list =
        payload?.result ??
        payload?.data ??
        payload?.receipts ??
        (Array.isArray(payload) ? payload : []);

      const items: any[] = Array.isArray(list) ? list : [];

      if (items.length <= 0) {
        setNoMoreData(true);
        if (pageNumber > 1) {
          setPage(pageNumber - 1);
        } else {
          setData([]);
        }
        return;
      }

      if (items.length < itemsPerPage) {
        setNoMoreData(true);
      } else {
        setNoMoreData(false);
      }
      setData(items);
    } catch (err: any) {
      console.log(err);
      setError(err?.response?.data?.message || err.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setPage(1);
    setNoMoreData(false);
    fetchReceipts(1);
  };

  const handlePrev = () => {
    if (page > 1) {
      const next = page - 1;
      setNoMoreData(false);
      setPage(next);
      fetchReceipts(next);
    }
  };

  const handleNext = () => {
    if (!noMoreData) {
      const next = page + 1;
      setPage(next);
      fetchReceipts(next);
    }
  };

  const columns =
    data.length > 0
      ? Object.keys(data[0]).filter((k) => typeof data[0][k] !== "object")
      : [];

  const columnLabels: Record<string, string> = {
    receiptDate: "Receipt Date",
    receiptNo: "Receipt No",
    memberCode: "Member Code",
    membername: "Member Name",
    memberName: "Member Name",
    amount: "Amount",
    paymentMode: "Payment Mode",
  };

  const formatHeader = (col: string) =>
    columnLabels[col] ??
    col
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (c) => c.toUpperCase());

  const isOnline = (item: any) =>
    String(item?.paymentMode ?? "").trim().toLowerCase() === "online";

  const formatCell = (val: any) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return String(val);
  };

  return (
    <div className="basePriceContainer">
      <h2 className="h2header basePrice">RECEIPT LIST</h2>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "flex-end",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label htmlFor="startDate" style={{ marginBottom: 4 }}>
            Start Date
          </label>
          <input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label htmlFor="endDate" style={{ marginBottom: 4 }}>
            End Date
          </label>
          <input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className="submit-btn"
          disabled={loading || !startDate || !endDate}
          style={{ padding: "8px 16px" }}
        >
          {loading ? "Loading..." : "Submit"}
        </button>
      </form>

      {error && <p style={{ color: "red" }}>{error}</p>}

      {submitted && (
        <>
          <div className="table-responsive">
            <table className="tbl styled-table" border={1}>
              <thead>
                <tr>
                  {columns.length > 0 ? (
                    columns.map((col) => (
                      <th key={col} style={{ minWidth: 120 }}>
                        {formatHeader(col)}
                      </th>
                    ))
                  ) : (
                    <th>No Data</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={Math.max(columns.length, 1)}>Loading...</td>
                  </tr>
                )}
                {!loading && data.length === 0 && (
                  <tr>
                    <td colSpan={Math.max(columns.length, 1)}>
                      No receipts found for the selected date range.
                    </td>
                  </tr>
                )}
                {!loading &&
                  data.map((item, index) => (
                    <tr
                      key={index}
                      className={isOnline(item) ? "online-row" : undefined}
                    >
                      {columns.map((col) => (
                        <td
                          key={col}
                          className={
                            col === "paymentMode" && isOnline(item)
                              ? "online-mode"
                              : undefined
                          }
                        >
                          {formatCell(item[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="page-buttons-container">
            <button
              disabled={page === 1 || loading}
              onClick={handlePrev}
              style={{ width: 50, padding: 4 }}
            >
              Prev
            </button>
            <button
              disabled={noMoreData || loading}
              onClick={handleNext}
              style={{ width: 50, padding: 4 }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ReceiptList;
